import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PlayAnimation from '@/components/PlayAnimation';
import PlayExportFrames from '@/components/PlayExportFrames';
import { exportPlayAsPDF, exportPlayAsPNG, shareViaWhatsApp } from '@/lib/exportPlay';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/constants/testIds';
import { putPlay, getPlay as getCachedPlay } from '@/lib/offlineDb';
import { ChevronLeft, ThumbsUp, ThumbsDown, Volume2, Brain, Trash2, Edit, Languages, Zap, FileDown, Image as ImageIcon, Share2, Sparkles, ClipboardList, MessageSquare, Calendar } from 'lucide-react';
import ReviewsPanel from '@/components/ReviewsPanel';
import { speak as ttsSpeak, primeTTS } from '@/lib/tts';
import { toast } from 'sonner';

export default function PlayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [play, setPlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [roster, setRoster] = useState([]);
  const [displayMode, setDisplayMode] = useState('photo'); // 'photo' | 'initials' | 'number'
  // F4↔F6 integration: detect if this play is part of an assignment for
  // the user's team. Cheap call (returns small list) — runs once per play.
  const [matchingAssignment, setMatchingAssignment] = useState(null);
  const exportRefs = useRef([]);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'png' | 'wa' | null
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    api.get(`/plays/${id}`)
      .then(r => {
        setPlay(r.data);
        // Mirror to IndexedDB for offline access (includes enrichments + translations).
        putPlay(r.data).catch(() => {});
      })
      .catch(async () => {
        // Offline fallback → use the cached copy if present.
        const cached = await getCachedPlay(id).catch(() => null);
        if (cached) setPlay(cached);
      })
      .finally(() => setLoading(false));
    api.get('/roster').then(r => setRoster(r.data.players || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    setTranslated(null);
    if (!play || lang === 'es') return;
    const cached = play.translations?.[lang];
    if (cached) { setTranslated(cached); return; }
    // Auto-translate when switching to a non-original language and no cache exists
    let cancelled = false;
    (async () => {
      setTranslating(true);
      try {
        const r = await api.post(`/translate/play/${id}?lang=${lang}`);
        if (!cancelled) setTranslated(r.data.translation);
      } catch {
        // silent fail — original text remains visible
      } finally {
        if (!cancelled) setTranslating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [play, lang, id]);

  // F5 study tracking: heartbeat every 15s while the play is open, plus a
  // final flush on unmount. Silently ignored if the user is not a player.
  useEffect(() => {
    if (!id) return;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += 15;
      api.post('/study/track', { play_id: id, time_spent_seconds: 15 }).catch(() => {});
    }, 15000);
    return () => {
      clearInterval(interval);
      // Mark some progress on close so the coach sees they at least opened it
      api.post('/study/track', { play_id: id, time_spent_seconds: 0, progress_pct: 25 }).catch(() => {});
    };
  }, [id]);

  // Look up assignments that include this play
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get('/assignments')
      .then(r => {
        if (cancelled) return;
        const match = (r.data?.assignments || []).find(a => (a.play_ids || []).includes(id));
        setMatchingAssignment(match || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const translate = async () => {
    if (lang === 'es') return;
    setTranslating(true);
    try {
      const r = await api.post(`/translate/play/${id}?lang=${lang}`);
      setTranslated(r.data.translation);
      toast.success(r.data.cached ? t('cached_translation') : t('translated_by_ai'));
    } catch (e) {
      toast.error(t('translation_error'));
    } finally {
      setTranslating(false);
    }
  };

  const giveFeedback = async (worked) => {
    try {
      await api.post(`/plays/${id}/feedback`, { worked });
      toast.success(worked ? t('feedback_success_yes') : t('feedback_success_no'));
    } catch {
      toast.error(t('feedback_error'));
    }
  };

  const predict = async () => {
    setPredicting(true);
    try {
      const r = await api.post(`/ai/predict/${id}`, { defense: 'man', language: lang });
      setPrediction(r.data);
    } catch {
      toast.error(t('prediction_error'));
    } finally {
      setPredicting(false);
    }
  };

  const speak = () => {
    // Critical: only speak when text + voice locale are guaranteed in sync.
    // If the UI is in a non-source language but the auto-translation hasn't
    // arrived yet, we would otherwise read the Spanish text with an English
    // voice — the exact regression the user reported. Guard against it.
    const enriched = play?.enrichments?.[lang]?.audio_text;
    const text = enriched || translated?.audio_text || (lang === (play?.original_lang || 'es') ? play?.audio_text : null);
    if (!text) {
      if (translating) toast.info(t('translating'));
      return;
    }
    primeTTS(lang);
    ttsSpeak({ text, lang });
  };

  const deletePlay = async () => {
    if (!window.confirm(t('delete_confirm'))) return;
    await api.delete(`/plays/${id}`);
    toast.success(t('deleted'));
    navigate('/playbook');
  };

  const handleExportPDF = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('pdf');
    try {
      await exportPlayAsPDF(displayPlay, exportRefs.current, { coachName: user?.name || '' });
      toast.success(t('pdf_generated'));
    } catch (e) {
      console.error(e);
      toast.error(t('pdf_export_error'));
    } finally {
      setExporting(null);
    }
  };

  const handleExportPNG = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('png');
    try {
      await exportPlayAsPNG(displayPlay, exportRefs.current);
      toast.success(t('image_downloaded'));
    } catch (e) {
      console.error(e);
      toast.error(t('image_export_error'));
    } finally {
      setExporting(null);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!exportRefs.current?.length) return toast.error(t('frames_not_ready'));
    setExporting('wa');
    try {
      const blob = await exportPlayAsPNG(displayPlay, exportRefs.current);
      const filename = `${(displayPlay.name || 'jugada').replace(/[^\w]+/g, '_')}.png`;
      const shared = await shareViaWhatsApp(displayPlay, blob, filename);
      if (!shared) toast.info(t('whatsapp_open_attach'));
    } catch (e) {
      console.error(e);
      toast.error(t('share_error'));
    } finally {
      setExporting(null);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const r = await api.post(`/translate/play/${id}/enrich?lang=${lang}&force=true`);
      // Merge enrichment into the in-memory play so the animation re-renders
      // with the richer step descriptions (and the TTS narrates them too).
      setPlay(prev => prev ? {
        ...prev,
        enrichments: { ...(prev.enrichments || {}), [lang]: r.data.enrichment },
      } : prev);
      toast.success(t('enrichment_success'));
    } catch (e) {
      toast.error(t('enrichment_error'));
    } finally {
      setEnriching(false);
    }
  };

  if (loading) return <Layout><div className="text-gray-400">{t('loading')}</div></Layout>;
  if (!play) return <Layout><div className="text-gray-400">{t('not_found')}</div></Layout>;

  const isOwner = user?.role === 'admin' || play.owner_id === user?.id;
  const isPlayer = user?.role === 'player';
  const rawName = translated?.name || play.name;
  const purchasedSuffix = /\s*\(Comprada\)\s*$/i;
  const isPurchased = !!play.purchased_from || purchasedSuffix.test(play.name || '');
  const displayName = isPurchased ? (rawName || '').replace(purchasedSuffix, '') : rawName;
  // Prefer enriched audio_text when available so "Listen narration" reads the
  // richer didactic intro instead of the short tactical shorthand.
  const enrichedAudio = play.enrichments?.[lang]?.audio_text;
  const displayDescription = enrichedAudio || translated?.description || play.description;

  // Build position→roster map (PG=1, SG=2, SF=3, PF=4, C=5). Prefer lowest number per position.
  const positionOrder = ['PG', 'SG', 'SF', 'PF', 'C'];
  const rosterByPos = {};
  positionOrder.forEach((pos, i) => {
    const candidates = roster.filter(p => p.position === pos).sort((a, b) => a.number - b.number);
    if (candidates.length) rosterByPos[String(i + 1)] = candidates[0];
  });
  const initialsOf = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '?';

  // Apply translated steps + enriched (if available) + roster auto-mapping
  const enrichment = play.enrichments?.[lang];
  const baseStepsForText = enrichment?.steps?.length
    ? play.steps.map(s => {
        const enr = enrichment.steps.find(es => es.step_number === s.step_number);
        return enr?.description ? { ...s, description: enr.description } : s;
      })
    : (translated?.steps?.length
        ? play.steps.map(s => {
            const tr = translated.steps.find(ts => ts.step_number === s.step_number);
            return tr ? { ...s, description: tr.description } : s;
          })
        : play.steps);
  const sourceSteps = baseStepsForText;
  const enrichedSteps = (sourceSteps || []).map(st => ({
    ...st,
    players: (st.players || []).map(pl => {
      if (pl.team !== 'offense') return pl;
      // Skip if already has explicit player assigned (custom play with photo)
      if (pl.photo_url || pl.player_name) {
        if (displayMode === 'number') return { ...pl, photo_url: null, label: pl.label };
        if (displayMode === 'initials' && pl.player_name) return { ...pl, photo_url: null, label: initialsOf(pl.player_name) };
        return pl;
      }
      // Auto-map: try roster by position label "1"–"5"
      const rp = rosterByPos[pl.label];
      if (!rp) return pl;
      if (displayMode === 'number') return { ...pl, label: String(rp.number) };
      if (displayMode === 'initials') return { ...pl, label: initialsOf(rp.name) };
      // photo mode (default)
      return { ...pl, photo_url: rp.photo_url || null, player_name: rp.name, label: String(rp.number) };
    }),
  }));
  const displayPlay = { ...play, steps: enrichedSteps };
  // When enrichment exists for the current UI language, the steps already contain
  // the right-language text — tell PlayAnimation to skip its own auto-translation
  // by pretending the play's source language matches the UI language. This avoids
  // PlayAnimation overwriting our enriched descriptions with stale translation cache.
  if (enrichment) {
    displayPlay.original_lang = lang;
  }

  return (
    <Layout>
      <Link to="/playbook" className="inline-flex items-center gap-1 text-gold hover:underline mb-4 text-sm uppercase tracking-wider font-h">
        <ChevronLeft className="w-4 h-4" /> {t('nav_playbook')}
      </Link>

      {/* On tablets (md ≤ width ≤ lg-1) the sidebar stacks below the court so
          the play diagram fills the screen width. On lg+ (iPad landscape /
          desktop) we keep the 2/3 + 1/3 split. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">
                {play.category} · {play.subcategory} · {play.difficulty}
              </div>
              <h1 className="font-h uppercase text-4xl sm:text-5xl leading-tight">
                {displayName}
                {isPurchased && <span className="text-sm text-gold ms-2 font-mono align-middle">{t('purchased_badge')}</span>}
              </h1>
            </div>
            <div className="font-mono text-gold border-2 border-gold/60 rounded-md px-3 py-2 text-lg">
              {Math.round(play.success_rate * 100)}%
            </div>
          </div>

          {/* F4↔F6 integration: assignment banner + "ask coach about this play" */}
          {matchingAssignment && (
            <div
              data-testid="play-assignment-banner"
              className="mb-4 bg-gold/10 border border-gold/50 rounded-lg p-3 flex items-center gap-3 flex-wrap"
            >
              <ClipboardList className="w-5 h-5 text-gold flex-shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gold">
                  {t('play_part_of_task') || 'Esta jugada es parte de tu tarea'}
                </div>
                <div className="font-h uppercase text-sm">
                  {matchingAssignment.title}
                  {matchingAssignment.due_date && (
                    <span className="ms-2 text-xs text-gray-300 font-mono normal-case">
                      <Calendar className="w-3 h-3 inline mb-0.5 me-0.5" />
                      {new Date(matchingAssignment.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to="/assignments"
                data-testid="play-go-assignments"
                className="text-xs px-3 py-1.5 rounded border border-gold/60 text-gold hover:bg-gold/10 inline-flex items-center gap-1 font-h uppercase tracking-wider"
              >
                {t('view') || 'Ver'}
              </Link>
            </div>
          )}
          {user?.role === 'player' && user?.coach_id && (
            <Link
              to={`/notes?to=${user.coach_id}`}
              data-testid="play-ask-coach"
              className="mb-4 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-gold/40 text-gold hover:bg-gold/10 font-h uppercase tracking-wider"
            >
              <MessageSquare className="w-3 h-3" /> {t('play_ask_about') || 'Preguntar al coach sobre esta jugada'}
            </Link>
          )}

          <PlayAnimation play={displayPlay} />

          {/* Display mode toggle */}
          <div className="mt-3 flex items-center justify-center gap-2 bg-card-steel border border-gold/30 rounded-lg p-2">
            <span className="text-[10px] uppercase tracking-widest text-gold font-mono me-2">{t('show_as')}</span>
            {[
              { id: 'photo', label: t('photo') },
              { id: 'initials', label: t('initials') },
              { id: 'number', label: t('number') },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setDisplayMode(m.id)}
                data-testid={`display-mode-${m.id}`}
                className={`px-3 py-1.5 rounded text-xs font-h uppercase tracking-wider transition border ${
                  displayMode === m.id ? 'bg-gold text-navy border-gold' : 'border-steel text-gray-300 hover:border-gold/60'
                }`}
              >{m.label}</button>
            ))}
          </div>

          <ReviewsPanel playId={id} currentUserId={user?.id} />
        </div>

        <div className="space-y-4">
          <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('description_label')}</div>
            <p className="text-gray-200 leading-relaxed">{displayDescription}</p>
            {lang !== 'es' && (
              <button onClick={translate} disabled={translating} data-testid="translate-btn"
                className="mt-3 text-xs px-3 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1">
                <Languages className="w-3 h-3" /> {translating ? '...' : translated ? `${t('translated_to')} ${lang.toUpperCase()}` : `${t('translate_with_ai')} (${lang.toUpperCase()})`}
              </button>
            )}
          </div>

          <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('works_against')}</div>
            <div className="flex flex-wrap gap-2">
              {(play.works_against || []).map((d, i) => (
                <span key={i} className="px-2.5 py-1 rounded-md border border-gold/40 text-gold text-xs uppercase tracking-wider">{d}</span>
              ))}
            </div>
          </div>

          <button
            onClick={speak}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition font-h uppercase tracking-wider text-sm"
          >
            <Volume2 className="w-4 h-4" /> {t('listen_narration')}
          </button>

          {!isPlayer && (
            <button
              data-testid={TID.aiPredict}
              disabled={predicting}
              onClick={predict}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-gold text-navy hover:bg-gold-light transition font-h uppercase tracking-wider text-sm font-semibold disabled:opacity-50"
            >
              <Brain className="w-4 h-4" /> {predicting ? '...' : t('predict_success')}
            </button>
          )}

          <Link
            to={`/simulator/${id}`}
            data-testid="open-simulator-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-gold/60 text-gold hover:bg-gold/10 transition font-h uppercase tracking-wider text-sm font-semibold">
            <Zap className="w-4 h-4" /> {t('tactical_simulator')}
          </Link>


          {/* Export & share — coaches/admin only */}
          {!isPlayer && (
            <div className="bg-card-steel border border-gold/30 rounded-lg p-3 space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">{t('export_share')}</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleExportPDF}
                  disabled={exporting !== null}
                  data-testid="export-pdf-btn"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition text-xs font-h uppercase tracking-wider disabled:opacity-50"
                >
                  <FileDown className="w-3.5 h-3.5" /> {exporting === 'pdf' ? '...' : 'PDF'}
                </button>
                <button
                  onClick={handleExportPNG}
                  disabled={exporting !== null}
                  data-testid="export-png-btn"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition text-xs font-h uppercase tracking-wider disabled:opacity-50"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> {exporting === 'png' ? '...' : t('image_label')}
                </button>
              </div>
              <button
                onClick={handleShareWhatsApp}
                disabled={exporting !== null}
                data-testid="share-whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-[#25D366] text-white hover:brightness-110 transition text-xs font-h uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                <Share2 className="w-3.5 h-3.5" /> {exporting === 'wa' ? t('generating_dots') : t('share_whatsapp_btn')}
              </button>
            </div>
          )}

          {prediction && (
            <div className="bg-gold/10 border-2 border-gold rounded-lg p-4">
              <div className="font-h text-4xl text-gold">{prediction.success_pct}%</div>
              <div className="text-xs uppercase tracking-widest text-gold/80 mb-2">{t('ai_prediction')}</div>
              <p className="text-sm text-gray-200">{prediction.reason}</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => giveFeedback(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-green-500/50 text-green-400 hover:bg-green-500/10 transition text-sm font-h uppercase tracking-wider"
            >
              <ThumbsUp className="w-4 h-4" /> {t('worked')}
            </button>
            <button
              onClick={() => giveFeedback(false)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-alert/50 text-alert hover:bg-alert/10 transition text-sm font-h uppercase tracking-wider"
            >
              <ThumbsDown className="w-4 h-4" /> {t('didnt_work')}
            </button>
          </div>

          {isOwner && !play.is_global && (
            <div className="flex gap-2 pt-3 border-t border-steel">
              <Link
                to={`/editor/${play.id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition text-sm font-h uppercase"
              >
                <Edit className="w-4 h-4" /> {t('edit')}
              </Link>
              <button
                onClick={deletePlay}
                className="px-4 py-2.5 rounded-md border border-alert/50 text-alert hover:bg-alert/10 transition text-sm font-h uppercase"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Offscreen frames for export */}
      <PlayExportFrames
        steps={enrichedSteps}
        onReady={(refs) => { exportRefs.current = refs; }}
      />
    </Layout>
  );
}
